import { produce } from 'immer';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface SidebarSettings {
  disabled: boolean;
  isHoverOpen: boolean;
}

export interface SidebarStore {
  isOpen: boolean;
  isHover: boolean;
  /** Suppresses the collapsed icon rail so a workspace owns the full width. Transient — never persisted. */
  focusMode: boolean;
  settings: SidebarSettings;
  toggleOpen: () => void;
  setIsOpen: (isOpen: boolean) => void;
  setIsHover: (isHover: boolean) => void;
  setFocusMode: (focusMode: boolean) => void;
  getOpenState: () => boolean;
  setSettings: (settings: Partial<SidebarSettings>) => void;
}

export const useSidebarStore = create(
  persist<SidebarStore>(
    (set, get) => ({
      isOpen: true,
      isHover: false,
      focusMode: false,
      settings: { disabled: false, isHoverOpen: false },

      toggleOpen: () => {
        set({ isOpen: !get().isOpen, isHover: false });
      },

      setIsOpen: (isOpen: boolean) => {
        set({ isOpen, isHover: false });
      },

      setIsHover: (isHover: boolean) => {
        set({ isHover });
      },

      setFocusMode: (focusMode: boolean) => {
        set({ focusMode, isHover: false });
      },

      getOpenState: () => {
        return get().isOpen;
      },

      setSettings: (settings: Partial<SidebarSettings>) => {
        set(
          produce((state: SidebarStore) => {
            state.settings = { ...state.settings, ...settings };
          }),
        );
      },
    }),
    {
      name: 'examing-sidebar',
      storage: createJSONStorage(() => localStorage),
      // focusMode is route-driven and must never survive a reload (a persisted
      // `true` would hide the nav on an unrelated page). Persist only isOpen +
      // settings.
      partialize: (state) =>
        ({ isOpen: state.isOpen, settings: state.settings }) as unknown as SidebarStore,
    },
  ),
);
